<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

import { useAuth } from '@/composables/useAuth'
import useAuthStore from '@/stores/authStore'

import FLanguageMenu from '@/components/feature/FLanguageMenu/FLanguageMenu.vue'
import AccountSwitcher from './AccountSwitcher.vue'
import { useNavigationBadges } from './useNavigationBadges'

import logoBlack from '@/assets/logo_black.png'
import mittAmiliLogo from '@/assets/mitt_amili_logo.svg'

import mdiAccountCircleOutline from '~icons/mdi/account-circle-outline'
import mdiCashMultiple from '~icons/mdi/cash-multiple'
import mdiChatQuestionOutline from '~icons/mdi/chat-question-outline'
import mdiClose from '~icons/mdi/close'
import mdiCreditCardOutline from '~icons/mdi/credit-card-outline'
import mdiFilePdfBox from '~icons/mdi/file-pdf-box'
import mdiLogout from '~icons/mdi/logout'
import mdiMenu from '~icons/mdi/menu'
import mdiTextBoxOutline from '~icons/mdi/text-box-outline'

// The signed-in shell: top bar (logo, staff chip, account switcher, language menu, burger) and the
// right-hand drawer. Mounted only while a token is selected, so the drawer cannot outlive a logout.
interface NavigationItem {
  title: string
  icon: typeof mdiMenu
  to: string
  badge?: number
  dividerBefore?: boolean
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { mobile } = useDisplay()
const authStore = useAuthStore()
const { signOut } = useAuth()
const { unreadTickets, unhandledOverpayments, total } = useNavigationBadges()

const drawer = ref(false)

const claims = computed(() => authStore.selectedToken?.payload)

// The bar hides on the login route even while signed in (a ?token= deep link renders that view
// with a token selected).
const showBar = computed(() => route.name !== 'Authenticate')

const items = computed<NavigationItem[]>(() => [
  { title: 'common.cases', icon: mdiCreditCardOutline, to: '/' },
  { title: 'common.my_profile', icon: mdiAccountCircleOutline, to: '/profile' },
  { title: 'common.support', icon: mdiChatQuestionOutline, to: '/support', badge: unreadTickets.value },
  { title: 'common.debt_summary', icon: mdiFilePdfBox, to: '/summary' },
  { title: 'overpayments.title', icon: mdiCashMultiple, to: '/overinbetalningar', badge: unhandledOverpayments.value },
  { title: 'common.about_site', icon: mdiTextBoxOutline, to: '/om-webbplatsen', dividerBefore: true },
])

function navigateHome() {
  drawer.value = false
  if (route.name !== 'Home') void router.push('/')
}

function logout() {
  drawer.value = false
  // Clearing the tokens is enough: useAuthEffects flushes the caches and redirects to /authenticate.
  signOut()
}
</script>

<template>
  <v-navigation-drawer
    v-model="drawer"
    class="rounded-s-xl"
    location="right"
    width="320"
    disable-resize-watcher
  >
    <div class="d-flex flex-column h-100">
      <div class="d-flex justify-space-between mt-2 mr-2">
        <v-img
          :class="{ 'cursor-pointer': route.name !== 'Home' }"
          :src="logoBlack"
          alt="Amili"
          class="ml-4"
          max-width="56px"
          @click="navigateHome"
        />

        <v-btn
          :aria-label="t('common.close')"
          :icon="mdiClose"
          size="small"
          variant="flat"
          @click="drawer = false"
        />
      </div>

      <v-list
        class="mt-1"
        nav
      >
        <template
          v-for="item in items"
          :key="item.to"
        >
          <v-divider
            v-if="item.dividerBefore"
            class="my-2"
          />
          <v-list-item
            :prepend-icon="item.icon"
            :title="t(item.title)"
            :to="item.to"
            class="align-center"
          >
            <template
              v-if="item.badge"
              #append
            >
              <v-badge
                :content="item.badge"
                color="red_dark"
                max="9"
                inline
              />
            </template>
          </v-list-item>
        </template>

        <v-divider class="my-2" />
        <v-list-item
          :prepend-icon="mdiLogout"
          :title="t('common.logout')"
          @click="logout"
        />
      </v-list>

      <div class="flex-grow-1" />

      <account-switcher
        v-if="mobile"
        class="pa-2 mb-2 w-100"
        @close="drawer = false"
      />
    </div>
  </v-navigation-drawer>

  <v-app-bar
    v-if="showBar"
    color="primary"
    density="comfortable"
  >
    <v-app-bar-title>
      <v-img
        :src="mittAmiliLogo"
        alt="Mitt Amili"
        class="cursor-pointer"
        max-width="100px"
        @click="navigateHome"
      />
    </v-app-bar-title>

    <!-- Staff signed in as this customer: the claims are typed on the token payload. -->
    <v-chip
      v-if="claims?.signed_in_by_u_id != null"
      color="orange"
      variant="tonal"
    >
      {{ t('common.as_x', { x: claims.u_name }) }}
      <span
        v-if="claims.readonly"
        class="ml-1"
      >
        ({{ t('common.read') }})
      </span>
    </v-chip>

    <account-switcher v-if="!mobile" />

    <f-language-menu />

    <v-app-bar-nav-icon
      v-if="!drawer"
      :aria-label="t('common.menu')"
      @click="drawer = true"
    >
      <v-badge
        :content="total"
        :model-value="total > 0"
        color="red_dark"
        max="9"
      >
        <v-icon :icon="mdiMenu" />
      </v-badge>
    </v-app-bar-nav-icon>
  </v-app-bar>
</template>
